<script setup lang="ts">
import type { RouteLocationRaw } from '#vue-router';
import SeminarCard from './SeminarCard.vue';
import { useElementSize } from '@vueuse/core';
import { type FragmentType, graphql } from '~/gql';

const props = defineProps<{
  seminars: FragmentType<typeof SeminarFragment>[];
  seminarsLink: RouteLocationRaw;
  category: string;
}>();

const SeminarFragment = graphql(`
  fragment ProductLpTemplateSeminarSectionSeminarFragment on Seminar {
    id
    ...ProductLpTemplateSeminarCardSeminarFragment
  }
`);

const seminars = useComputedFragment(SeminarFragment, () => props.seminars);
const carousel = ref<HTMLElement>();
const { width: carouselWidth } = useElementSize(carousel);
const itemsToShow = computed(() => Math.max(1, carouselWidth.value / 380));
const needToAnimate = computed(() => itemsToShow.value < seminars.value.length);
const currentSlide = ref<number>(0);
watch(
  needToAnimate,
  (needToAnimate) => {
    if (!needToAnimate) return;
    currentSlide.value = Math.floor(seminars.value.length / 2);
  },
  { immediate: true }
);
const snapAlign = computed(() => {
  if (needToAnimate.value || seminars.value.length % 2 === 1) {
    return 'center';
  }
  return 'center-even';
});
</script>

<template>
  <div v-if="seminars.length > 0" :class="$style.section">
    <p :class="$style.section__heading">セミナー</p>
    <p :class="$style.section__subheading">
      {{ category }}とは？がわかるマネーセミナー
    </p>
    <Carousel
      ref="carousel"
      v-model="currentSlide"
      :class="$style.section__carousel"
      :autoplay="needToAnimate ? 3000 : 0"
      :transition="800"
      :items-to-show="itemsToShow"
      :snap-align="snapAlign"
      :wrap-around="needToAnimate"
      :mouse-drag="needToAnimate"
      :touch-drag="needToAnimate"
      pause-autoplay-on-hover
    >
      <Slide v-for="s in seminars" :key="s.id">
        <SeminarCard :seminar="s" />
      </Slide>
    </Carousel>
    <p :class="$style.section__link">
      <NuxtLink :href="seminarsLink">セミナーをもっと見る</NuxtLink>
    </p>
  </div>
</template>

<style lang="scss" module>
@use './constants' as *;
.section {
  width: 100%;

  &__heading {
    width: 100%;
    text-align: center;
    font-size: 40px;
    font-weight: bold;
    @include mobile-only {
      font-size: 24px;
    }
  }
  &__subheading {
    margin-top: 12px;
    color: $color-text-and-object-accent-primary;
    font-weight: bold;
    width: 100%;
    text-align: center;
    margin-bottom: 36px;
    @include mobile-only {
      font-size: 16px;
      margin-top: 8px;
      margin-bottom: 24px;
    }
  }

  &__carousel {
    width: 100%;
  }

  &__link {
    margin: 12px auto 0;
    padding: 0 40px;
    width: 100%;
    max-width: $content-width;
    text-align: end;
    text-decoration: underline;
    color: $color-text-and-object-accent-primary;
    font-weight: bold;
  }
}
</style>
